// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Button, Flex, Group, Loader, Modal, Stack, Text, Title } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { GrantPatientsModal } from './GrantPatientsModal';
import { useRevokeSwePatients } from './hooks/useRevokeSwePatients';
import { type SwePatientRow, useSwePatients } from './hooks/useSwePatients';
import { RevokeAllModal } from './RevokeAllModal';

interface SwePatientListProps {
  sweId: string;
  sweName: string;
}

function EmptyState({ sweName }: { sweName: string }): JSX.Element {
  return (
    <Flex direction="column" justify="center" align="center" p="md" mih={200}>
      <Text c="dimmed" fw={500} ta="center">
        No patients granted access
      </Text>
      <Text c="dimmed" size="sm" mt="xs" ta="center">
        {sweName} has not been granted access to any patients yet.
      </Text>
    </Flex>
  );
}

export function SwePatientList({ sweId, sweName }: SwePatientListProps): JSX.Element {
  const { patients, loading, reload } = useSwePatients(sweId);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<SwePatientRow[]>([]);
  const [revokeConfirmModalOpen, setRevokeConfirmModalOpen] = useState(false);
  const [revokeAllModalOpen, setRevokeAllModalOpen] = useState(false);
  const handleRevokeSuccess = useCallback((): void => {
    setSelectedPatients([]);
    setRevokeConfirmModalOpen(false);
    setRevokeAllModalOpen(false);
    reload();
  }, [reload]);
  const { revoke, loading: revoking } = useRevokeSwePatients(sweId, handleRevokeSuccess);
  const currentPatientIds = useMemo(() => patients.map((p) => p.id), [patients]);
  const selectedPatientIds = useMemo(() => selectedPatients.map((p) => p.id), [selectedPatients]);

  async function handleConfirmRevoke(): Promise<void> {
    await revoke(selectedPatientIds);
  }

  const handleConfirmRevokeAll = useCallback(async (): Promise<void> => {
    await revoke();
  }, [revoke]);

  if (loading) {
    return (
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Granted Patients</Title>
          <Group gap="xs">
            <Button variant="light" disabled data-testid="revoke-selected-button">
              Revoke selected
            </Button>
            <Button variant="light" color="red" disabled data-testid="revoke-all-button">
              Revoke all
            </Button>
            <Button
              variant="light"
              onClick={() => setGrantModalOpen(true)}
              disabled={loading}
              data-testid="grant-patients-button"
            >
              Grant patients
            </Button>
          </Group>
        </Group>
        <Loader size="sm" />
      </Stack>
    );
  }

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>
            {patients.length === 0 ? 'Granted Patients' : `Granted Patients (${patients.length})`}
          </Title>
          <Group gap="xs">
            <Button
              variant="light"
              onClick={() => setRevokeConfirmModalOpen(true)}
              disabled={selectedPatients.length === 0 || revoking}
              data-testid="revoke-selected-button"
            >
              Revoke selected
            </Button>
            <Button
              variant="light"
              color="red"
              onClick={() => setRevokeAllModalOpen(true)}
              disabled={patients.length === 0 || loading || revoking}
              data-testid="revoke-all-button"
            >
              Revoke all
            </Button>
            <Button
              variant="light"
              onClick={() => setGrantModalOpen(true)}
              disabled={loading}
              data-testid="grant-patients-button"
            >
              Grant patients
            </Button>
          </Group>
        </Group>

        {patients.length === 0 ? (
          <EmptyState sweName={sweName} />
        ) : (
          <DataTable
            fetching={loading}
            minHeight={200}
            records={patients}
            idAccessor={(row) => row.id}
            selectedRecords={selectedPatients}
            onSelectedRecordsChange={setSelectedPatients}
            striped
            highlightOnHover
            fz="sm"
            noRecordsText="No patients granted access"
            columns={[
              {
                accessor: 'name',
                title: 'Name',
              },
              {
                accessor: 'mrn',
                title: 'MRN',
                render: (row: SwePatientRow) => row.mrn || '—',
              },
              {
                accessor: 'dob',
                title: 'Date of Birth',
                render: (row: SwePatientRow) => {
                  if (!row.dob) {
                    return '—';
                  }
                  const date = new Date(row.dob);
                  return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  });
                },
              },
            ]}
          />
        )}
      </Stack>

      {grantModalOpen && (
        <GrantPatientsModal
          sweId={sweId}
          sweName={sweName}
          currentPatientIds={currentPatientIds}
          onGranted={reload}
          onClose={() => setGrantModalOpen(false)}
        />
      )}

      <Modal
        opened={revokeConfirmModalOpen}
        onClose={() => setRevokeConfirmModalOpen(false)}
        title="Revoke Access"
        data-testid="revoke-confirmation-modal"
      >
        <Stack gap="md">
          <Text>
            Revoke access for {selectedPatients.length} patient{selectedPatients.length === 1 ? '' : 's'}?
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="light" onClick={() => setRevokeConfirmModalOpen(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmRevoke} loading={revoking} data-testid="revoke-confirm-button">
              Revoke
            </Button>
          </Group>
        </Stack>
      </Modal>

      <RevokeAllModal
        opened={revokeAllModalOpen}
        sweName={sweName}
        patientCount={patients.length}
        loading={revoking}
        onConfirm={handleConfirmRevokeAll}
        onClose={() => setRevokeAllModalOpen(false)}
      />
    </>
  );
}
